import axios, { AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { toast } from '@/utils/toast'
import { confirmDialog } from '@/utils/confirm'
import { useUserStore } from '@/stores/user'
import router from '@/router'

const baseURL = import.meta.env.VITE_API_BASE || '/api'

const service = axios.create({
  baseURL,
  timeout: 15000,
})

// 请求拦截器：自动附加 JWT
service.interceptors.request.use(
  (config) => {
    const userStore = useUserStore()
    if (userStore.token && config.headers) {
      config.headers.Authorization = `Bearer ${userStore.token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// 响应拦截器：统一处理 code
service.interceptors.response.use(
  (response: AxiosResponse<BaseResponse>) => {
    const res = response.data
    if (res.code !== 200) {
      toast.error(res.message || '请求失败')
      // token 失效（与 nest-admin 错误码一致：1101 / 1105）
      if (res.code === 1101 || res.code === 1105) {
        const userStore = useUserStore()
        userStore.logout()
        toast.warning('登录状态已失效，请重新登录')
      }
      return Promise.reject(new Error(res.message || '请求失败'))
    }
    return response
  },
  (error: AxiosError<BaseResponse>) => {
    // 后端返回 401：登录已失效，退出登录并弹出确认框引导重新登录。
    if (error.response?.status === 401) {
      handleUnauthorized()
      return Promise.reject(error)
    }
    const msg = error.response?.data?.message || error.message || '网络异常'
    toast.error(msg)
    return Promise.reject(error)
  },
)

// 防止并发多个 401 时重复弹出确认框
let authDialogShown = false

async function handleUnauthorized() {
  const userStore = useUserStore()
  userStore.logout()

  if (authDialogShown)
    return
  authDialogShown = true

  try {
    const ok = await confirmDialog({
      title: '登录状态已失效',
      message: '请登陆后重试',
      confirmText: '立即登录',
      cancelText: '取消',
    })
    if (ok) {
      // 跳转到登录页，并携带当前路径以便登录后回跳
      router
        .push({ name: 'login', query: { redirect: window.location.pathname } })
        .catch(() => {})
    }
  } finally {
    authDialogShown = false
  }
}

/**
 * 统一 request 工具：默认只返回 data 字段
 *  - 传 isReturnResult: false 返回完整 BaseResponse（含 code/message/data）
 *  - 注意：第一个重载的 isReturnResult 必须是「必需的 false」，否则默认调用会错误匹配返回 BaseResponse
 */
export async function request<T = unknown>(
  config: AxiosRequestConfig & { isReturnResult: false },
): Promise<BaseResponse<T>>
export async function request<T = unknown>(
  config: AxiosRequestConfig & { isReturnResult?: true },
): Promise<T>
export async function request<T = unknown>(
  url: string,
  config?: AxiosRequestConfig & { isReturnResult?: boolean },
): Promise<T>
export async function request<T = unknown>(
  arg1: string | (AxiosRequestConfig & { isReturnResult?: boolean }),
  arg2?: AxiosRequestConfig & { isReturnResult?: boolean },
): Promise<T> {
  let config: AxiosRequestConfig & { isReturnResult?: boolean }
  if (typeof arg1 === 'string') {
    config = { ...arg2, url: arg1 }
  } else {
    config = arg1
  }
  const { isReturnResult = true, ...rest } = config
  const response = await service.request<BaseResponse<T>>(rest)
  return (isReturnResult ? response.data.data : response.data) as T
}

export default service